import React from "react";

import { InfoTooltip, SortableList } from "../BuilderUtils";
import { stepSummary } from "../StepStoryboard";
import { stepKind } from "../stepKinds";
import { buildChapters, chapterTitleOf } from "../../territory_intro/chapters";
import { layerLabel } from "../../terrain3d/layerSpecs";

/**
 * LE MODULE « TERRITOIRE 3D » : son décor, et le cheminement qu'on y trace.
 *
 * LE TERRITOIRE VIENT DE L'ATELIER, pas du module. Il l'a longtemps porté
 * lui-même, ce qui faisait deux endroits nommant la même chose : s'ils
 * divergeaient, la carte se reconstruisait en pleine séance. Il n'est donc
 * choisi ici que tant que la page n'appartient à aucun atelier porteur de
 * carte — sinon il est déduit, et seulement montré.
 */
export default function TerritoryInspector({
  page, onPatchContent, ctx, onOpenStep, onAddStep, onRemoveStep, onReorderSteps,
}) {
  const { territories = [], pageTerritory } = ctx;
  const content = page.content || {};
  const steps = content.steps || [];
  const territory = territories.find((t) => t.id === content.territory_id);
  // Pour l'affichage seulement : compter les chapitres réellement nommés, pour
  // ne pas annoncer « en 1 chapitre » un module qui n'en a aucun.
  const namedChapters = buildChapters(steps).filter((chapter) => chapter.title);

  const toggleLayer = (layerId) => {
    const current = content.layers || [];
    onPatchContent({
      layers: current.includes(layerId) ? current.filter((l) => l !== layerId) : [...current, layerId],
    });
  };

  return (
    <>
      <div className="builder-form-grid">
        <label className="builder-label">
          <span className="field-label-row">
            Territoire
            <InfoTooltip text="Imposé par le ou les ateliers qui utilisent ce module — un atelier n'a qu'un territoire, et il ne change pas. Tant que la page n'est dans aucun atelier, choisissez le décor sur lequel composer." />
          </span>
          <select
            className="builder-input"
            value={content.territory_id || ""}
            disabled={!pageTerritory?.free}
            onChange={(e) => onPatchContent({ territory_id: e.target.value })}
          >
            <option value="">— choisir —</option>
            {territories.map((t) => (
              <option key={t.id} value={t.id}>{t.label || t.id}</option>
            ))}
          </select>
          {pageTerritory?.conflicting && (
            <span className="builder-hint builder-hint-error">
              Ce module est utilisé par des ateliers de territoires différents
              ({pageTerritory.owners.map((o) => `${o.id} → ${o.territory}`).join(", ")}).
              Dupliquez-le pour en avoir une version par territoire.
            </span>
          )}
          {!pageTerritory?.free && !pageTerritory?.conflicting && (
            <span className="builder-hint">Imposé par l'atelier qui l'utilise.</span>
          )}
        </label>

        <label className="builder-label">
          <input
            type="checkbox"
            checked={content.fog_enabled !== false}
            onChange={(e) => onPatchContent({ fog_enabled: e.target.checked })}
          />
          &nbsp;Brouillard activé
        </label>
      </div>

      {content.territory_id && (
        <div style={{ marginTop: 16 }}>
          <div className="field-label-row" style={{ marginBottom: 8 }}>
            <span className="builder-label-text">Couches disponibles pour ce module</span>
            <InfoTooltip text="Sous-ensemble des couches du territoire chargées par ce module. Chaque étape choisit ensuite lesquelles sont visibles parmi celles-ci." />
          </div>
          <div className="step-editor-layer-checks">
            {/* Libellés du registre plutôt que les identifiants bruts du
                datapack : l'auteur cochait « geology », « withdrawals ». */}
            {(territory?.layers || []).map((layerId) => (
              <label key={layerId} className="builder-label builder-label-inline">
                <input
                  type="checkbox"
                  checked={(content.layers || []).includes(layerId)}
                  onChange={() => toggleLayer(layerId)}
                />
                {" "}{layerLabel(layerId)}
              </label>
            ))}
            {(territory?.layers || []).length === 0 && (
              <div className="builder-empty">Aucune couche disponible pour ce territoire.</div>
            )}
          </div>
        </div>
      )}

      <div style={{ marginTop: 24 }}>
        <div className="builder-section-header">
          <h3 className="builder-subsection-title">
            <span className="field-label-row">
              Étapes du cheminement ({steps.length}
              {namedChapters.length > 0 && ` en ${namedChapters.length} chapitre${namedChapters.length > 1 ? "s" : ""}`})
              <InfoTooltip text="Séquence linéaire d'écrans parcourus par le joueur via le bouton Suivant : message, graphique, ou quizz — chacun avec sa propre vue caméra sur la carte 3D. Les étapes qui se suivent avec le même chapitre forment un groupe, affiché à la place du décompte d'étapes pendant la séance." />
            </span>
          </h3>
          <button className="builder-btn-secondary" disabled={!content.territory_id} onClick={onAddStep}>
            + Ajouter une étape
          </button>
        </div>

        {!content.territory_id && (
          <div className="builder-empty">Choisissez d'abord un territoire pour pouvoir ajouter des étapes.</div>
        )}
        {content.territory_id && steps.length === 0 && (
          <div className="builder-empty">Aucune étape. Cliquez sur « + Ajouter une étape ».</div>
        )}
        {content.territory_id && steps.length > 0 && (
          <div className="builder-pile">
            <SortableList
              items={steps.map((step, idx) => ({ id: step.step_id || `step_${idx}`, step }))}
              onReorder={(items) => onReorderSteps(items.map((i) => i.step))}
              renderItem={(item, idx) => {
                const step = item.step;
                const kind = stepKind(step.kind);
                // Le nom n'est répété que là où le chapitre COMMENCE : sur
                // chaque ligne, il transformerait la pile en colonne de redites
                // où la coupure ne se verrait plus. C'est la coupure qu'on
                // vient vérifier ici, en réordonnant.
                const chapter = chapterTitleOf(step);
                const startsChapter = chapter && chapter !== chapterTitleOf(steps[idx - 1]);
                return (
                  <div className={`builder-pile-item${startsChapter ? " starts-chapter" : ""}`}>
                    {startsChapter && <span className="builder-pile-chapter">{chapter}</span>}
                    <span className="builder-pile-index">{idx + 1}</span>
                    <span className="builder-pile-sketch">{kind?.sketch}</span>
                    <div className="builder-pile-info">
                      <span className="builder-pile-id">{kind?.label || step.kind}</span>
                      <span className="builder-pile-title">{stepSummary(step)}</span>
                      {!step.camera && <span className="builder-pile-missing">⚠ Aucun cadrage capturé</span>}
                    </div>
                    <div className="builder-pile-actions">
                      <button className="builder-icon-btn" onClick={() => onOpenStep(idx)} title="Éditer cette étape">✏</button>
                      <button className="builder-icon-btn danger" onClick={() => onRemoveStep(idx)}>✕</button>
                    </div>
                  </div>
                );
              }}
            />
          </div>
        )}
      </div>
    </>
  );
}
